// harmonogramVykon.js — výpočty harmonogramu zákazky v režime „Výkon m²/týždeň“ (HARMONOGRAM_VYKON_V1, 26. 10. 2026)
// Jedno miesto pre zákazku › Harmonogram (HarmonogramTab.jsx) aj Rozpis › Stavba › Harmonogram (StavbaHarmonogram.jsx).
// Mirko 26. 10. 2026: „ano ale nech je prepojený na harmonogram zákazky OK?“ (Kronika mirko 3569, doplnok matrix 3571, GO mirko 3574).
// Kód je PRESUNUTÝ z HarmonogramTab.jsx bez zmeny výpočtu — obe obrazovky počítajú ten istý plán rovnako (0.J).
// m² poschodí sa tu nepočítajú — prichádzajú hotové z Výmer cez GET /api/harmonogram/:projectId (floors, aktivita).

export const PALETTE = ['#0369a1', '#15803d', '#b45309', '#7c3aed', '#0d9488', '#be185d', '#4d7c0f', '#b91c1c']
export const DAY = 86400000
export const DEFAULT_VYKON = 400 // default m²/týždeň pre novú partiu — VERÍME zadanému výkonu, žiadny strop

// ── dátumové helpery (ISO yyyy-mm-dd) ──
export const iso = d => {
  const y = d.getFullYear(), m = d.getMonth() + 1, dd = d.getDate()
  return y + '-' + String(m).padStart(2, '0') + '-' + String(dd).padStart(2, '0')
}
export const fromIso = s => new Date(s + 'T00:00:00')
export const addDays = (s, n) => iso(new Date(fromIso(s).getTime() + n * DAY))
export const fmtD = s => { if (!s) return ''; const d = fromIso(s); return d.getDate() + '.' + (d.getMonth() + 1) + '.' }
export const todayIso = () => iso(new Date())
export const isWeekendD = d => d.getDay() === 0 || d.getDay() === 6
export const mondayOf = s => { const d = fromIso(s); const wd = (d.getDay() + 6) % 7; return iso(new Date(d.getTime() - wd * DAY)) }
export const nextWork = s => { let d = fromIso(s); while (isWeekendD(d)) d = new Date(d.getTime() + DAY); return iso(d) }
export const fmtM2 = n => (Math.round(n) || 0).toLocaleString('sk-SK')

export const onVacation = (p, dayIso) => (p.dovolenky || []).some(v => v.from && v.to && dayIso >= v.from && dayIso <= v.to)

// ── simulácia poschodia: zostatok m² + partie + štart → koniec + dni dovolenky ──
// odKedy (HARMONOGRAM_VYKON_V1, 26. 10. 2026): { idPartie: 'yyyy-mm-dd' } — partia s vlastným začiatkom (pp.start) pracuje na poschodí až od neho;
// bez odKedy (všetci od začiatku poschodia) je výpočet presne ako predtým.
export function simulateFloor(remaining, startIso, partyIds, parties, odKedy) {
  const ps = (partyIds || []).map(id => parties.find(p => p.id === id)).filter(Boolean)
  if (remaining <= 0 || !ps.length || !startIso) return null
  let t = fromIso(startIso).getTime(), done = 0, guard = 0
  let lastWork = startIso
  const vacDays = []
  while (done < remaining && guard++ < 366) {
    const d = new Date(t), dIso = iso(d), wk = isWeekendD(d)
    let rate = 0, zacala = !odKedy
    for (const p of ps) {
      if (odKedy && odKedy[p.id] && dIso < odKedy[p.id]) continue
      zacala = true
      if (wk && !p.weekend) continue
      if (onVacation(p, dIso)) continue
      rate += (Number(p.vykon) || DEFAULT_VYKON) / 5
    }
    if (rate > 0) { done += rate; lastWork = dIso }
    else if (!wk && zacala) vacDays.push(dIso) // pracovný deň bez výkonu = dovolenka (nie deň, keď ešte žiadna partia nezačala)
    t += DAY
  }
  return { end: lastWork, vacDays }
}

// súvislé úseky z pola ISO dní → [{from,to}]
export function ranges(days) {
  const out = []
  for (const d of days) {
    const last = out[out.length - 1]
    if (last && (last.to === addDays(d, -1) || last.to === d)) last.to = d
    else out.push({ from: d, to: d })
  }
  return out
}

// vlastné začiatky partií na poschodí (pp.start); null = všetci od začiatku poschodia (pôvodný výpočet)
export function odKedyPartii(cfg) {
  const m = {}; let je = false
  for (const id of (cfg.parties || [])) { const o = (cfg.pp || {})[id]; if (o && o.start && cfg.start && o.start > cfg.start) { m[id] = o.start; je = true } }
  return je ? m : null
}

// ── odvodené dáta plánu: poschodia po objektoch (8.NP hore), hotové, zostatok, výpočet konca, časová os ──
export function vypocitajPlan(plan, floors) {
  if (!plan) return null
  const parties = plan.parties || []
  const fl = (floors || []).filter(f => f.m2 > 0)
  const skipped = (floors || []).filter(f => !(f.m2 > 0))
  // zoskupiť podľa objektu, poschodia zostupne (8.NP hore)
  const objs = {}
  for (const f of fl) {
    const key = f.obj + '|' + f.po
    const cfg = (plan.floors || {})[key] || {}
    const doneM2 = cfg.done ? f.m2 : Math.min(Number(cfg.doneM2) || 0, f.m2)
    const remaining = f.m2 - doneM2
    // ručný koniec (cfg.end) má prednosť pred výpočtom — Mirko 2026-06-10: „bez toho to nejde"
    const manualEnd = !cfg.done && cfg.end && (cfg.start || cfg.end)
    let sim = cfg.done ? null
      : manualEnd ? { end: cfg.end >= (cfg.start || cfg.end) ? cfg.end : cfg.start, vacDays: [], manual: true }
      : simulateFloor(remaining, cfg.start, cfg.parties, parties, odKedyPartii(cfg))
    // per-partia vlastné dátumy (cfg.pp) — koniec poschodia = najneskorší koniec partie
    if (!cfg.done && sim && (cfg.parties || []).length) {
      const pp = cfg.pp || {}
      const ends = cfg.parties.map(id => (pp[id] || {}).end || sim.end)
      sim = { ...sim, end: ends.reduce((a, b) => (a > b ? a : b)) }
    }
    ;(objs[f.obj] = objs[f.obj] || []).push({ ...f, key, cfg, doneM2, remaining, sim })
  }
  for (const o of Object.keys(objs)) objs[o].sort((a, b) => (parseInt(b.po) || 0) - (parseInt(a.po) || 0))

  const total = fl.reduce((s, f) => s + f.m2, 0)
  const doneTotal = Object.values(objs).flat().reduce((s, f) => s + f.doneM2, 0)
  const rows = Object.values(objs).flat()
  const ends = rows.filter(f => f.sim).map(f => f.sim.end)
  const predpoklad = ends.length ? ends.reduce((a, b) => (a > b ? a : b)) : null
  const unassigned = rows.filter(f => !f.cfg.done && (!f.cfg.parties?.length || !f.cfg.start)).length
  const rezerva = (predpoklad && plan.deadline) ? Math.round((fromIso(plan.deadline) - fromIso(predpoklad)) / DAY) : null

  // časová os
  const today = todayIso()
  const starts = rows.map(f => f.cfg.start).filter(Boolean)
    .concat(rows.map(f => f.cfg.doneFrom).filter(Boolean))
  if (plan.start) starts.push(plan.start)
  let min = starts.length ? starts.reduce((a, b) => (a < b ? a : b)) : today
  if (!plan.start && today < min) min = today
  min = mondayOf(min)
  let max = predpoklad || addDays(min, 42)
  if (plan.deadline && plan.deadline > max) max = plan.deadline
  max = addDays(max, 4)
  const span = Math.max(1, (fromIso(max) - fromIso(min)) / DAY)
  const pos = d => Math.min(100, Math.max(0, (fromIso(d) - fromIso(min)) / DAY / span * 100))
  const weeks = []
  for (let w = min; w < max; w = addDays(w, 7)) weeks.push(w)
  return { objs, skipped, total, doneTotal, predpoklad, rezerva, unassigned, min, max, span, pos, weeks, today, rows }
}

// úsek partie na poschodí — vlastný start/end (cfg.pp[id]) s fallbackom na poschodie/výpočet
export const segOf = (f, p) => {
  const o = ((f.cfg.pp || {})[p.id]) || {}
  const st = o.start || f.cfg.start
  let en = o.end || (f.sim ? f.sim.end : null)
  if (st && en && en < st) en = st
  return st && en ? { st, en, manual: !!(o.start || o.end) } : null
}

// ── AUTO-NAPLÁNOVAŤ (Mirko 2026-06-10): (1) Matrix sleduje vo VÝMERÁCH kto kde pracuje
// (pole Parťák pri bytoch + hotové byty) → tie poschodia priradí podľa reality.
// (2) Zvyšok reťazí: partie ZDVOJENE (2 na poschodie), sólo partia robí sama.
// Vracia návrh, nič neukladá: { prazdne } | { poschodi, bezPartii } | { poschodi, plan, zVymer, retazenych, priradene: [kľúče poschodí] }
const normName = s => (s || '').toLowerCase().trim()
export function navrhAutoPlan(plan, floors, aktivita) {
  const flAll = (floors || []).filter(f => f.m2 > 0)
  const queue0 = flAll.filter(f => { const c = (plan.floors || {})[f.obj + '|' + f.po] || {}; return !c.done && !(c.parties && c.parties.length) })
  if (!queue0.length) return { prazdne: true }

  const cfgs = { ...(plan.floors || {}) }
  const partiesAll = [...(plan.parties || [])]
  const findOrCreateParty = name => {
    let p = partiesAll.find(x => normName(x.name) === normName(name))
    if (!p) {
      p = { id: 'pt' + Math.random().toString(36).slice(2, 8), name, color: PALETTE[partiesAll.length % PALETTE.length], vykon: DEFAULT_VYKON, weekend: false, objekt: '', dovolenky: [], solo: false }
      partiesAll.push(p)
    }
    return p
  }

  // 1) Z VÝMER — kto kde pracuje
  const akt = {}
  for (const a of (aktivita || [])) { if (a.m2 > 0) (akt[a.obj + '|' + a.po] = akt[a.obj + '|' + a.po] || []).push(a) }
  const startBase = nextWork(plan.start || todayIso())
  let zVymer = 0
  for (const f of flAll) {
    const key = f.obj + '|' + f.po
    const c = cfgs[key] || {}
    const rows = akt[key] || []
    if (c.done || !rows.length) continue
    if (!(c.parties && c.parties.length)) {
      cfgs[key] = { ...c, parties: rows.map(a => findOrCreateParty(a.partak).id), start: c.start || startBase }
      zVymer++
    }
    const doneSum = rows.reduce((s, a) => s + (a.done_m2 || 0), 0)
    if (!(Number(cfgs[key].doneM2) > 0) && doneSum > 0) cfgs[key] = { ...cfgs[key], doneM2: Math.round(Math.min(doneSum, f.m2)) }
  }

  if (!partiesAll.length) return { poschodi: queue0.length, bezPartii: true }

  const remOf = f => {
    const c = cfgs[f.obj + '|' + f.po] || {}
    return f.m2 - Math.min(Number(c.doneM2) || 0, f.m2)
  }
  const queue = flAll
    .filter(f => { const c = cfgs[f.obj + '|' + f.po] || {}; return !c.done && !(c.parties && c.parties.length) })
    .sort((a, b) => a.obj === b.obj ? (parseInt(b.po) || 0) - (parseInt(a.po) || 0) : (a.obj < b.obj ? -1 : 1))

  // dostupnosť partie = deň po konci jej už priradených poschodí (vrátane práve priradených z Výmer)
  const avail = {}
  for (const p of partiesAll) {
    let a = startBase
    for (const f of flAll) {
      const c = cfgs[f.obj + '|' + f.po] || {}
      if (c.done || !c.start || !(c.parties || []).includes(p.id)) continue
      const sim = simulateFloor(remOf(f), c.start, c.parties, partiesAll, odKedyPartii(c))
      if (sim) { const n = nextWork(addDays(sim.end, 1)); if (n > a) a = n }
    }
    avail[p.id] = a
  }

  // 2) reťazenie zvyšku — ZDVOJENÉ partie (sólo robí sama)
  const okObj = (p, f) => !p.objekt || p.objekt === f.obj
  const newFloors = cfgs
  let retazenych = 0, guard = 0
  while (queue.length && guard++ < 500) {
    const cands = partiesAll
      .filter(p => queue.some(f => okObj(p, f)))
      .sort((a, b) => (avail[a.id] < avail[b.id] ? -1 : 1))
    if (!cands.length) break
    const p1 = cands[0]
    const group = [p1]
    if (!p1.solo) {
      const p2 = cands.find(p => p.id !== p1.id && !p.solo && queue.some(f => okObj(p1, f) && okObj(p, f)))
      if (p2) group.push(p2)
    }
    const idx = queue.findIndex(f => group.every(p => okObj(p, f)))
    if (idx < 0) break
    const f = queue.splice(idx, 1)[0]
    const key = f.obj + '|' + f.po
    const start = group.map(p => avail[p.id]).reduce((a, b) => (a > b ? a : b))
    newFloors[key] = { ...(newFloors[key] || {}), parties: group.map(p => p.id), start }
    const sim = simulateFloor(remOf(f), start, group.map(p => p.id), partiesAll)
    const next = sim ? nextWork(addDays(sim.end, 1)) : start
    group.forEach(p => { avail[p.id] = next })
    retazenych++
  }
  const priradene = queue0.map(f => f.obj + '|' + f.po).filter(k => (newFloors[k] && newFloors[k].parties || []).length)
  return { poschodi: queue0.length, plan: { ...plan, parties: partiesAll, floors: newFloors }, zVymer, retazenych, priradene }
}

// ── ÚPRAVY PLÁNU ťahaním (Rozpis › Stavba › Harmonogram; Mirko 26. 10. 2026, mirko 3586: „chytit bunku a prehadzovať po poschodiach
// natahovať“). Čisté funkcie: vracajú NOVÝ plán, pôvodný nemenia. Model je ten istý ako v zákazke › Harmonogram:
// poschodie má partie, začiatok (start) a koniec — vypočítaný z m² a súčtu výkonov partií, alebo ručný (end);
// pri viacerých partiách má partia vlastný úsek pp[id] = { start, end }.
const kopiaPlanu = (plan, key) => {
  const floors = { ...(plan.floors || {}) }
  const c = floors[key] || {}
  floors[key] = { ...c, parties: [...(c.parties || [])], pp: { ...(c.pp || {}) } }
  return { ...plan, parties: [...(plan.parties || [])], floors }
}
const upratajCfg = (c) => {
  if (c.parties.length > 1 && c.start && c.parties.every(id => c.pp[id] && c.pp[id].start)) {   // nikto nezačína na začiatku poschodia → posuň ho na najskorší
    const m = c.parties.map(id => c.pp[id].start).sort()[0]
    if (m > c.start) { c.start = m; for (const id of c.parties) if (c.pp[id].start === m) { const { start, ...zvysok } = c.pp[id]; if (Object.keys(zvysok).length) c.pp[id] = zvysok; else delete c.pp[id] } }
  }
  if (c.pp && !Object.keys(c.pp).length) delete c.pp
  if (!c.parties.length) { delete c.parties; delete c.start; delete c.end; delete c.pp }
  return c
}
// partia podľa mena (presne ako Auto-naplánovať); neexistujúca sa pridá s predvoleným výkonom
const normMeno = s => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim()
export function partiaPodlaMena(plan, name) {
  const p = (plan.parties || []).find(x => normMeno(x.name) === normMeno(name))
  if (p) return { plan, id: p.id }
  const n = { id: 'pt' + Math.random().toString(36).slice(2, 8), name, color: PALETTE[(plan.parties || []).length % PALETTE.length], vykon: DEFAULT_VYKON, weekend: false, objekt: '', dovolenky: [], solo: false }
  return { plan: { ...plan, parties: [...(plan.parties || []), n] }, id: n.id }
}
// úsek partie na poschodí: st–en; auto = koniec počíta Matrix z m² a výkonu (len keď je partia na poschodí sama)
export function nastavUsek(plan, key, pid, st, en, auto) {
  const n = kopiaPlanu(plan, key); const c = n.floors[key]
  if (!c.parties.includes(pid)) c.parties.push(pid)
  if (c.parties.length === 1) {
    c.start = st
    if (auto) delete c.end; else c.end = en < st ? st : en
    delete c.pp[pid]
  } else {
    zaciatokPoschodia(c, pid, st)
    c.pp[pid] = auto ? { start: st } : { start: st, end: en < st ? st : en }
  }
  upratajCfg(c); return n
}
// poschodie začne skôr (st) → ostatné partie si podržia svoj doterajší začiatok, nikto iný sa neposunie
function zaciatokPoschodia(c, pid, st) {
  if (c.start && st >= c.start) return
  if (c.start) for (const o of c.parties) if (o !== pid && !(c.pp[o] && c.pp[o].start)) c.pp[o] = { ...(c.pp[o] || {}), start: c.start }
  c.start = st
}
// partia odíde z poschodia
export function odoberZPoschodia(plan, key, pid) {
  const n = kopiaPlanu(plan, key); const c = n.floors[key]
  c.parties = c.parties.filter(x => x !== pid); delete c.pp[pid]
  if (c.parties.length === 1 && c.pp[c.parties[0]]) {   // zostala jedna partia → jej úsek je úsek poschodia
    const o = c.pp[c.parties[0]]; if (o.start) c.start = o.start; if (o.end) c.end = o.end; delete c.pp[c.parties[0]]
  }
  upratajCfg(c); return n
}
// partia príde na poschodie od dňa st; koniec dopočíta Matrix (pri dvoch partiách sa sčíta výkon → poschodie sa skráti)
export function pridajNaPoschodie(plan, key, pid, st) {
  const c0 = (plan.floors || {})[key] || {}
  if ((c0.parties || []).includes(pid)) return plan
  if (!(c0.parties || []).length || !c0.start) return nastavUsek(plan, key, pid, st, st, true)
  const n = kopiaPlanu(plan, key); const c = n.floors[key]
  delete c.pp[pid]   // zvyšok po partii, ktorá tu bola predtým
  c.parties.push(pid)
  if (st > c.start) c.pp[pid] = { start: st }; else zaciatokPoschodia(c, pid, st)
  delete c.end   // ručný koniec poschodia platil pre pôvodné partie — s novou partiou ho počíta Matrix
  upratajCfg(c); return n
}
export function presunNaPoschodie(plan, zKey, naKey, pid, st) {
  if (zKey === naKey) return plan
  return pridajNaPoschodie(odoberZPoschodia(plan, zKey, pid), naKey, pid, st)
}
// späť na výpočet: zruší ručný koniec partie (a poschodia, ak je na ňom sama)
export function automatickyKoniec(plan, key, pid) {
  const n = kopiaPlanu(plan, key); const c = n.floors[key]
  if (c.pp[pid]) { const st = c.pp[pid].start; if (st && st !== c.start) c.pp[pid] = { start: st }; else delete c.pp[pid] }
  delete c.end   // ručný koniec poschodia platil pre všetky jeho partie
  upratajCfg(c); return n
}
